"use client";

import Link from "next/link";
import { citySlug } from "@/lib/site";

// The India map exactly as the design draws it (Website-2026, "Group 6" in
// the USA Landing page V4 frame): a 560×623 outline with nine cities, each a
// white dot ringed in teal beside a white label pill.
//
// The outline is 31 vector paths in the file, merged into one and served as a
// static SVG — 170 KB raw, 60 KB gzipped and cached, rather than inlined into
// every render of this page. The markers stay in HTML on top so the labels
// use the site's own Outfit rather than falling back to a system font, as
// text inside an <img>-loaded SVG would.
//
// Positions are percentages taken straight from the design's node transforms,
// so the markers track the outline at any width.
export const GCC_MAP_CITIES = [
  { name: "New Delhi",   dot: [31.79, 23.92], pill: [26.61, 18.30, 11.96] },
  { name: "Gurugram",    dot: [29.46, 26.16], pill: [15.18, 24.56, 12.32] },
  { name: "Noida",       dot: [33.75, 26.16], pill: [36.61, 24.72,  8.75] },
  { name: "Bhubaneswar", dot: [61.79, 58.75], pill: [55.54, 53.13, 15.00] },
  { name: "Pune",        dot: [23.39, 63.88], pill: [17.68, 58.59,  8.04] },
  { name: "Hyderabad",   dot: [38.21, 67.26], pill: [32.32, 62.28, 13.04] },
  { name: "Mumbai",      dot: [18.04, 70.95], pill: [ 3.04, 69.18, 10.54] },
  { name: "Bengaluru",   dot: [26.79, 79.45], pill: [11.79, 77.85, 12.14] },
  { name: "Chennai",     dot: [41.79, 84.91], pill: [43.57, 83.15, 10.54] },
] as const;

export function GccIndiaMap({
  className = "",
  active = null,
  onHover,
}: {
  className?: string;
  /** Name of the city currently previewed, from GccFootprint. */
  active?: string | null;
  onHover?: (city: string | null) => void;
}) {
  return (
    <div
      className={`relative mx-auto w-full max-w-[560px] ${className}`}
      style={{ aspectRatio: "560 / 623" }}
    >
      {/* eslint-disable-next-line @next/next/no-img-element -- a flat SVG with
          no raster sources; the optimiser has nothing to do here. */}
      <img
        src="/images/gcc-v3/india-map.svg"
        alt="Map of India showing the cities where Table Space operates"
        className="h-full w-full select-none"
        draggable={false}
      />

      {GCC_MAP_CITIES.map((c) => (
        <div key={c.name}>
          {/* dot — white centre, 2px teal ring, as drawn */}
          {/* The dot answers to hover as well as the pill. aria-hidden stays:
              the pill beside it is the labelled link, so exposing the dot
              would read the city twice. Pointer-only enhancement. */}
          <span
            aria-hidden
            onMouseEnter={() => onHover?.(c.name)}
            onMouseLeave={() => onHover?.(null)}
            className={`absolute block cursor-pointer rounded-full border-2 transition-[transform,background-color,border-color] duration-200 ${
              active === c.name
                ? "border-brand-accent bg-brand-accent"
                : "border-brand-accent-bright bg-white"
            }`}
            style={{
              left: `${c.dot[0]}%`,
              top: `${c.dot[1]}%`,
              width: "3.57%",           // 20 / 560
              aspectRatio: "1 / 1",
              // The scale rides on top of the centring translate, so both
              // have to live in the same transform — a Tailwind scale-* class
              // would overwrite the translate and drop the dot off its city.
              transform: `translate(-50%, -50%) scale(${active === c.name ? 1.6 : 1})`,
            }}
          />
          {/* label pill. The design's are static; linking them costs nothing
              visually and makes the map usable. */}
          <Link
            href={`/locations/${citySlug(c.name)}`}
            onMouseEnter={() => onHover?.(c.name)}
            onMouseLeave={() => onHover?.(null)}
            onFocus={() => onHover?.(c.name)}
            onBlur={() => onHover?.(null)}
            className={`absolute flex items-center justify-center rounded-full px-2 text-[clamp(0.5rem,1.05vw,0.625rem)] font-medium leading-none shadow-[0_1px_3px_rgba(0,0,0,0.10)] transition-colors ${
              active === c.name ? "bg-brand-accent text-white" : "bg-white text-black hover:bg-brand-accent hover:text-white"
            }`}
            style={{
              left: `${c.pill[0]}%`,
              top: `${c.pill[1]}%`,
              minWidth: `${c.pill[2]}%`,
              height: "3.37%",          // 21 / 623
            }}
          >
            {c.name}
          </Link>
        </div>
      ))}
    </div>
  );
}
